import { FormEvent, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Context } from "../../models/context";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { GetGroups } from "../../services/groups";
import { GetProjects } from "../../services/projects";
import { GetStatsSummary, StatsSummaryI } from "../../services/stats";
import StatsTable from "./stats-components/StatsTable";
import StatsSettings from "./settings/StatsSettings";
import "./stats-page.css";

type EntityType = "groups" | "users" | "apps" | "projects";

type ListedEntity = {
  id: string;
  label: string;
};

type Props = {
  entityLabel: string;
  entitySingular?: string;
  entityType: EntityType;
};

// entity types with a real listing endpoint. "users" and "apps" don't have one, so those are looked up by id instead.
const LISTABLE_TYPES: EntityType[] = ["groups", "projects"];

export default function StatsTemplate({ entityLabel, entitySingular, entityType }: Props) {
  const context = useContext(Context);
  const navigate = useNavigate();

  const singular = entitySingular ?? entityLabel;
  const isListable = LISTABLE_TYPES.includes(entityType);

  const [entities, setEntities] = useState<ListedEntity[]>([]);
  const [searchId, setSearchId] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<StatsSummaryI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!context.user) navigate("/login");
  }, [context.user, navigate]);

  useEffect(() => {
    if (!context.user || !isListable) return;

    if (entityType === "groups") {
      new GetGroups()
        .requestFromStart(context.user, 50)
        .then((groups) => setEntities(groups.map((g) => ({ id: g.groupId, label: g.groupName }))))
        .catch(() => setEntities([]));
    } else if (entityType === "projects") {
      new GetProjects()
        .request(context.user)
        .then((projects) => setEntities(projects.map((p) => ({ id: p.name, label: p.name }))))
        .catch(() => setEntities([]));
    }
  }, [context.user, entityType, isListable]);

  const loadStats = async (id: string) => {
    if (!context.user || !id) return;
    setLoading(true);
    setError(null);
    setSelectedId(id);
    try {
      const result = await new GetStatsSummary(id).request(context.user);
      setSummary(result);
    } catch {
      setSummary(null);
      setError(`Could not load stats for "${id}".`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    loadStats(searchId.trim());
  };

  return (
    <div id="stats-groups-page">
      <div id="stats-groups-columns">

        {/* Left column — list */}
        <div id="groups-list-column">
          <div id="groups-list-wrapper" className="container">
            <div id="groups-header">
              <Link to=".." relative="path">
                <button className="back-icon">
                  <ArrowBackIosNewRoundedIcon fontSize="large" />
                </button>
              </Link>
              <h1>{entityLabel}</h1>
            </div>

            {isListable ? (
              <div id="groups-accordion">
                <div id="groups-accordion-wrapper">
                  {entities.map((entity) => (
                    <div
                      key={entity.id}
                      className={`entity-item${selectedId === entity.id ? " selected" : ""}`}
                      onClick={() => loadStats(entity.id)}
                    >
                      {entity.label}
                    </div>
                  ))}
                </div>
                {entities.length === 0 && (
                  <div id="no-groups">
                    <p>No {entityLabel.toLowerCase()} found.</p>
                  </div>
                )}
              </div>
            ) : (
              <div id="groups-accordion">
                <form id="stats-id-search" onSubmit={handleSearchSubmit}>
                  <label htmlFor="stats-id-input">{singular} ID</label>
                  <input
                    id="stats-id-input"
                    type="text"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    placeholder={`Enter a ${singular.toLowerCase()} id`}
                  />
                  <input type="submit" value="Search" className="section-button" />
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Right column — table */}
        <div id="stats-table-column">
          <div id="stats-table-wrapper" className="container">
            <div className="table-header">
              <h1>Data</h1>
              <button type="button" className="settings-icon" onClick={() => setSettingsOpen(true)}>
                <SettingsRoundedIcon fontSize="large" />
              </button>
            </div>
            <div id="table-wrapper">
              {loading && <p>Loading...</p>}
              {!loading && error && <p>{error}</p>}
              {!loading && !error && !summary && (
                <p>Select a {singular.toLowerCase()} to view its stats.</p>
              )}
              {!loading && !error && summary && <StatsTable summary={summary} />}
            </div>
          </div>
        </div>

      </div>

      <StatsSettings
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        entityType={entityType}
      />
    </div>
  );
}
